import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Card, CardBody, CardFooter, CardGroup, CardHeader } from "../../components/card/card";
import CONFIG from "../../config/config";
import { getFlagEmoji, getAuthHeaders } from "../../misc.js";
import genres from "../../config/genres.js";

// Fetch channels from the API (token attached)
const fetchChannels = async () => {
  const response = await fetch(`${CONFIG.API_URL}/channel`, {
    headers: {
      ...getAuthHeaders()
    }
  });
  return response.json();
};

const ChannelList = () => {
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editModalShow, setEditModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelNameInput, setChannelNameInput] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [videoBackgroundFile, setVideoBackgroundFile] = useState(null);
  const [shortBackgroundFile, setShortBackgroundFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [thumbnailPreviews, setThumbnailPreviews] = useState([]);
  const [videoBackgroundPreview, setVideoBackgroundPreview] = useState(null);
  const [shortBackgroundPreview, setShortBackgroundPreview] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    language: "",
    voiceId: "",
    youtube: { channelId: "" },
    type: "", 
    genre: "",  
    channel_username: "",
  });
  

  // List of supported languages
  const languages = [
    { code: "en", name: "English" },
    { code: "es", name: "Spanish" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "zh", name: "Chinese" },
    { code: "jp", name: "Japanese" },
    { code: "ko", name: "Korean" },
    { code: "pt", name: "Portuguese" }
    // Add more languages as needed
  ];

  // List of channel types (should match your backend enum)
  const channelTypes = [
    { value: "xvid", label: "Xvid" },
    { value: "quiz", label: "Quiz" },
    { value: "song_reel", label: "Song Reel" },
  ];

  // Retrieve the user role from localStorage
  const role = localStorage.getItem("role");

  useEffect(() => {
    const loadChannels = async () => {
      try {
        setIsLoading(true);
        const response = await fetchChannels();
        if (response.success) {
          setChannels(response.data);
        }
      } catch (error) {
        console.error("Error fetching channels:", error);
        setError("Failed to load channels. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadChannels();
  }, []);

  // Cleanup preview URLs on unmount or when previews change
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      thumbnailPreviews.forEach((preview) => URL.revokeObjectURL(preview));
      if (videoBackgroundPreview) URL.revokeObjectURL(videoBackgroundPreview);
      if (shortBackgroundPreview) URL.revokeObjectURL(shortBackgroundPreview);
    };
  }, [logoPreview, thumbnailPreviews, videoBackgroundPreview, shortBackgroundPreview]);

  // Handler for logo file change in edit modal
  const handleEditLogoChange = (e) => {
    const file = e.target.files[0];
    setLogoFile(file);
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setLogoPreview(previewURL);
    } else {
      setLogoPreview(null);
    }
  };

  // Handler for thumbnails file change in edit modal
  const handleEditThumbnailChange = (e) => {
    const files = Array.from(e.target.files);
    setThumbnailFiles(files);
    // Revoke previous previews
    thumbnailPreviews.forEach((preview) => URL.revokeObjectURL(preview));
    const previews = files.map((file) => URL.createObjectURL(file));
    setThumbnailPreviews(previews);
  };

  // Handler for video background file change
  const handleEditVideoBackgroundChange = (e) => {
    const file = e.target.files[0];
    setVideoBackgroundFile(file);
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setVideoBackgroundPreview(previewURL);
    } else {
      setVideoBackgroundPreview(null);
    }
  };

  // New: Handler for short background file change in edit modal
  const handleEditShortBackgroundChange = (e) => {
    const file = e.target.files[0];
    setShortBackgroundFile(file);
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setShortBackgroundPreview(previewURL);
    } else {
      setShortBackgroundPreview(null);
    }
  };

  const handleEditClick = (channel) => {
    setSelectedChannel(channel);
    setEditFormData({
      name: channel.name,
      language: channel.language,
      voiceId: channel.voiceId,
      youtube: channel.youtube || { channelId: "" },
      type: channel.type,  // Set the current channel type
      genre: channel.genre || "", // Set the current genre
      channel_username: channel.channel_username,
    });
    // Reset file inputs and previews when opening modal
    setLogoFile(null);
    setThumbnailFiles([]);
    setVideoBackgroundFile(null);
    setShortBackgroundFile(null);
    setLogoPreview(null);
    setThumbnailPreviews([]);
    setVideoBackgroundPreview(null);
    setShortBackgroundPreview(null);
    setEditModalShow(true);
  };

  const handleDeleteClick = (channel) => {
    setSelectedChannel(channel);
    setChannelNameInput("");
    setDeleteModalShow(true);
  };

  const handleEditSave = async () => {
    try {
      // First update basic channel data including genre
      const updateResponse = await fetch(`${CONFIG.API_URL}/channel/${selectedChannel._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify(editFormData)
      });
      if (updateResponse.ok) {
        let updatedChannels = channels.map((channel) =>
          channel._id === selectedChannel._id ? { ...channel, ...editFormData } : channel
        );
  
        // Upload images if logo or thumbnails have been selected
        if (logoFile || thumbnailFiles.length > 0) {
          const formData = new FormData();
          if (logoFile) {
            formData.append("logo", logoFile);
          }
          thumbnailFiles.forEach((file) => {
            formData.append("thumbnails", file);
          });
  
          const uploadResponse = await fetch(`${CONFIG.API_URL}/channel/${selectedChannel._id}/upload-images`, {
            method: "POST",
            // Do not set Content-Type header when using FormData.
            headers: {
              ...getAuthHeaders()
            },
            body: formData
          });
  
          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            // Update the channel with new logo and thumbnails if returned from the server
            updatedChannels = updatedChannels.map((channel) =>
              channel._id === selectedChannel._id ? uploadData.data : channel
            );
          } else {
            console.error("Failed to upload images");
          }
        }

        // New: Upload short background if a file has been selected
        if (shortBackgroundFile) {
          const shortBgFormData = new FormData();
          shortBgFormData.append("short_background", shortBackgroundFile);
          const shortBgUploadResponse = await fetch(`${CONFIG.API_URL}/channel/${selectedChannel._id}/upload-short-background`, {
            method: "POST",
            headers: {
              ...getAuthHeaders()
            },
            body: shortBgFormData
          });
          if (shortBgUploadResponse.ok) {
            const shortBgUploadData = await shortBgUploadResponse.json();
            updatedChannels = updatedChannels.map((channel) =>
              channel._id === selectedChannel._id
                ? { ...channel, short_background: shortBgUploadData.url }
                : channel
            );
          } else {
            console.error("Failed to upload short background");
          }
        }
  
        // Upload video background if a file has been selected
        if (videoBackgroundFile) {
          const videoFormData = new FormData();
          videoFormData.append("video_background", videoBackgroundFile);
          const videoUploadResponse = await fetch(`${CONFIG.API_URL}/channel/${selectedChannel._id}/upload-video-background`, {
            method: "POST",
            headers: {
              ...getAuthHeaders()
            },
            body: videoFormData
          });
          if (videoUploadResponse.ok) {
            const videoUploadData = await videoUploadResponse.json();
            // Update channel with the new video background URL if returned from the server
            updatedChannels = updatedChannels.map((channel) =>
              channel._id === selectedChannel._id ? { ...channel, video_background: videoUploadData.url } : channel
            );
          } else {
            console.error("Failed to upload video background");
          }
        }
  
        setChannels(updatedChannels);
        setEditModalShow(false);
        // Reset file states and previews
        setLogoFile(null);
        setThumbnailFiles([]);
        setVideoBackgroundFile(null);
        setShortBackgroundFile(null);
        setLogoPreview(null);
        setThumbnailPreviews([]);
        setVideoBackgroundPreview(null);
        setShortBackgroundPreview(null);
      } else {
        console.error("Failed to update channel");
      }
    } catch (error) {
      console.error("Error updating channel:", error);
    }
  };
  
  const handleDeleteConfirm = async () => {
    if (channelNameInput === selectedChannel.name) {
      try {
        const response = await fetch(`${CONFIG.API_URL}/channel/${selectedChannel._id}`, {
          method: "DELETE",
          headers: {
            ...getAuthHeaders()
          }
        });
        if (response.ok) {
          const updatedChannels = channels.filter(channel => channel._id !== selectedChannel._id);
          setChannels(updatedChannels);
          setDeleteModalShow(false);
        } else {
          console.error("Failed to delete channel");
        }
      } catch (error) {
        console.error("Error deleting channel:", error);
      }
    }
  };

  if (isLoading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (error) {
    return <div className="alert alert-danger mt-5">{error}</div>;
  }

  return (
    <div className="container">
      <h1>Channels</h1>
      <CardGroup className="mt-3">
        {channels.map((channel) => (
          <div key={channel._id} className="col-md-3 mb-4">
            <Card>
              <CardHeader>
                <h5 className="card-title">{channel.name}</h5>
              </CardHeader>
              <CardBody>
                <label className="form-label">Language:</label>
                <p className="card-text mt-2">
                  <span role="img" aria-label="Language">
                    {getFlagEmoji(channel.language)}
                  </span>{" "}
                  {channel.language}
                </p>
                <label className="form-label">Voice ID:</label>
                <p className="card-text">
                  <small className="text-muted">{channel.voiceId}</small>
                </p>
                <label className="form-label">Channel Type:</label>
                <p className="card-text">
                  <small className="text-muted">{channel.type}</small>
                </p>
                <label className="form-label">Channel ID:</label>
                <p className="card-text">
                  <small className="text-muted">{channel.youtube?.channelId}</small>
                </p>
                <label className="form-label">Genre:</label>
                <p className="card-text">
                  <small className="text-muted">{channel.genre}</small>
                </p>
                <label className="form-label">Channel Username:</label>
                <p className="card-text">
                  <small className="text-muted">{channel.channel_username}</small>
                </p>
              </CardBody>
              <CardFooter className="d-flex justify-content-between">
                {role === "admin" && (
                  <>
                    <button 
                      className="btn btn-primary w-50 me-1" 
                      onClick={() => handleEditClick(channel)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger w-50 ms-1" 
                      onClick={() => handleDeleteClick(channel)}
                    >
                      Delete
                    </button>
                  </>
                )}
              </CardFooter>
            </Card>
          </div>
        ))}
      </CardGroup>

      {/* Edit Modal */}
      {editModalShow && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Channel</h5>
                <button type="button" className="btn-close" onClick={() => setEditModalShow(false)}></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label className="form-label">Channel Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Language</label>
                    <select
                      className="form-select"
                      value={editFormData.language}
                      onChange={(e) => setEditFormData({ ...editFormData, language: e.target.value })}
                    >
                      <option value="">Select a language</option>
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Channel Type</label>
                    <select
                      className="form-select"
                      value={editFormData.type}
                      onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                    >
                      <option value="">Select a channel type</option>
                      {channelTypes.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* New: Genre select in edit modal */}
                  <div className="mb-3">
                    <label className="form-label">Genre</label>
                    <select
                      className="form-select"
                      value={editFormData.genre}
                      onChange={(e) => setEditFormData({ ...editFormData, genre: e.target.value })}
                    >
                      <option value="">Select a genre</option>
                      {genres.map((g, index) => (
                        <option key={index} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Voice ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.voiceId}
                      onChange={(e) => setEditFormData({ ...editFormData, voiceId: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Channel ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.youtube?.channelId}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          youtube: { ...editFormData.youtube, channelId: e.target.value }
                        })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Channel Username</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.channel_username}
                      onChange={(e) => setEditFormData({ ...editFormData, channel_username: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Upload Logo</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleEditLogoChange}
                    />
                  </div>
                  {logoPreview && (
                    <div className="mb-3">
                      <label className="form-label">Logo Preview:</label>
                      <div>
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          style={{ maxWidth: "200px", marginTop: "10px" }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Upload Thumbnails</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      multiple
                      onChange={handleEditThumbnailChange}
                    />
                  </div>
                  {thumbnailPreviews.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label">Thumbnail Previews:</label>
                      <div className="d-flex flex-wrap" style={{ marginTop: "10px" }}>
                        {thumbnailPreviews.map((preview, index) => (
                          <img
                            key={index}
                            src={preview}
                            alt={`Thumbnail ${index + 1}`}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              marginRight: "10px",
                              marginBottom: "10px",
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {/* New: Short Background Input */}
                  <div className="mb-3">
                    <label className="form-label">Upload Short Background</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleEditShortBackgroundChange}
                    />
                  </div>
                  {shortBackgroundPreview && (
                    <div className="mb-3">
                      <label className="form-label">Short Background Preview:</label>
                      <div>
                        <img
                          src={shortBackgroundPreview}
                          alt="Short Background Preview"
                          style={{ maxWidth: "200px", marginTop: "10px" }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Upload Video Background</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleEditVideoBackgroundChange}
                    />
                  </div>
                  {videoBackgroundPreview && (
                    <div className="mb-3">
                      <label className="form-label">Video Background Preview:</label>
                      <div>
                        <img
                          src={"https://api.moatoverflow.com/statics/" + videoBackgroundPreview}
                          alt="Video Background Preview"
                          style={{ maxWidth: "200px", marginTop: "10px" }}
                        />
                      </div>
                    </div>
                  )}
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditModalShow(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleEditSave}>Save changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalShow && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Channel</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteModalShow(false)}></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete this channel? Please type the channel name to confirm.
                </p>
                <input
                  type="text"
                  className="form-control"
                  value={channelNameInput}
                  onChange={(e) => setChannelNameInput(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDeleteModalShow(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDeleteConfirm}
                  disabled={channelNameInput !== selectedChannel?.name}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelList;
